import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  DroppableContainer,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { MdDragIndicator } from '@teamworksdev/icons';
import React, { RefObject, useState } from 'react';
import {
  DisabledTracks,
  resolveDropHighlight,
  resolveTrackCollision,
  resolveTrackDrop,
} from '../track-drag';
import { isHiddenSubTrack } from '../track-rows';
import { TrackForEdit, TrackPreview } from '../types';
import { BOTTOM_PADDING, TRACK_HEIGHT } from '../utils';
import { TrackSidePanelItem } from './TrackSidePanelItem';

export function Tracks({
  scrollRef,
  onScroll,
  tracks,
  lastTrackIndex,
  collapsedTracks,
  toggleTrackCollapse,
  setTrackForEdit,
  onReorderTracks,
  openAddTrackDrawer,
  refetchTracks,
  openMoveSubTrack,
  subTrackReorganizationFlag,
  canEditTracks,
  children,
}: {
  scrollRef: RefObject<HTMLDivElement>;
  onScroll: React.UIEventHandler<HTMLDivElement>;
  tracks: TrackPreview[];
  // Index of the final track row in the whole shared row model (active or
  // archived) — see the comment in TrackPanel.tsx for why this must not be
  // scoped to "last active track" once an archived/additional-teams section
  // can follow.
  lastTrackIndex: number;
  collapsedTracks: string[];
  toggleTrackCollapse: (trackId: string) => void;
  setTrackForEdit: (forEdit: TrackForEdit) => void;
  // Fires the reorder write for a same-group drop; the optimistic order is
  // an overlay owned next to `tracksForRender` — Tracks renders the prop.
  onReorderTracks: (tracks: TrackPreview[]) => void;
  openAddTrackDrawer: (open: boolean, parentTrack?: TrackPreview) => void;
  refetchTracks: () => void;
  // Opens the move flow for a sub-track dropped on another parent's group.
  // `destinationTrack` is omitted by the context-menu entry, which has not
  // chosen a destination yet.
  openMoveSubTrack: (
    subTrack: TrackPreview,
    destinationTrack?: TrackPreview,
  ) => void;
  // Cross-parent drops are a move only while the feature is on; with the flag
  // off the drop takes the path it took before the feature existed.
  subTrackReorganizationFlag: boolean;
  // Every write a drop can produce — the reorder, the move, and the edit behind
  // the context menu — is gated by Tracks Edit server-side, so a viewer without
  // it gets no drop targets at all rather than a drag that ends in a rejection.
  canEditTracks: boolean;
  children?: React.ReactNode;
}) {
  // Hide sub-tracks while dragging a parent track
  const [disabledTracks, setDisabledTracks] = useState<DisabledTracks>(null);
  const [trackToSort, setTrackToSort] = useState<TrackPreview | null>(null);
  // The group (by parent track id) currently under the pointer, so its parent
  // row + sub-track list can render as a single highlighted drop zone.
  const [overGroupId, setOverGroupId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor));

  function handleDragStart(event: DragStartEvent) {
    const track = tracks.find((t) => t.id === event.active.id);
    if (!track) return;
    setTrackToSort(track);
    setDisabledTracks(track.parentTrackId ? 'parents' : 'children');
  }

  function handleDragOver(event: DragOverEvent) {
    const { over } = event;
    const targetId = resolveTrackCollision({
      candidateId: over?.id == null ? null : String(over.id),
      items: tracks,
      disabledTracks,
      trackToSort,
      moveSubTrackEnabled: subTrackReorganizationFlag,
    });
    setOverGroupId(targetId);
  }

  function handleDragEnd(event: DragEndEvent) {
    // active is the track being dragged
    // over is the track being dragged over when dropping the active track
    const { active, over } = event;

    const outcome = resolveTrackDrop({
      items: tracks,
      activeId: String(active.id),
      overId: over?.id == null ? null : String(over.id),
      moveSubTrackEnabled: subTrackReorganizationFlag,
    });

    if (outcome.kind === 'move') {
      // The destination is already known, so the modal opens pre-filled.
      openMoveSubTrack(outcome.subTrack, outcome.destinationTrack);
    } else if (outcome.kind === 'reorder') {
      onReorderTracks(outcome.tracks);
    }

    setDisabledTracks(null);
    setTrackToSort(null);
    setOverGroupId(null);
  }

  // One predicate, two consumers: the `disabled` prop a row renders with, and
  // the container filter in collision detection below.
  //
  // It is the filter — not `disabled` — that makes a row undroppable.
  // `@dnd-kit/sortable`'s normalizeLocalDisabled maps a boolean `disabled` to
  // {draggable: <the boolean>, droppable: false}, so a disabled row keeps its
  // droppable registration and reaches collision detection like any other
  // container. Sharing the predicate is what keeps the two in step: a row that
  // renders disabled is exactly a row the filter drops.
  //
  // Without Tracks Edit there is no drop to resolve: every drop is a write the
  // server refuses, so no row is a target and none of them render a drag handle
  // either.
  const isDropTarget = (trackId: string) =>
    canEditTracks &&
    resolveTrackCollision({
      candidateId: trackId,
      items: tracks,
      disabledTracks,
      trackToSort,
      moveSubTrackEnabled: subTrackReorganizationFlag,
    }) !== null;

  const customCollisionDetection: CollisionDetection = ({
    active,
    collisionRect,
    droppableContainers,
    ...args
  }) => {
    // Filter out containers that are not valid targets for this drag before
    // passing to collision detection. A dragged sub-track keeps the other
    // parents' groups in play, so the filter now drops a container only when it
    // is genuinely off limits (see resolveTrackCollision).
    const enabledDroppableContainers = droppableContainers.filter(
      (container: DroppableContainer) => isDropTarget(String(container.id)),
    );

    const collisions = closestCenter({
      active,
      collisionRect,
      droppableContainers: enabledDroppableContainers,
      ...args,
    });

    // A parent row and its sub-track list are one drop zone, so a collision
    // with any member of a group is reported as the group's parent track id.
    // Remapping collapses the rest of that group's rows onto the same id, so
    // they are dropped from the tail of the list rather than reported as
    // separate, equally-close targets.
    const resolvedTargets = new Set<string>();
    return collisions.flatMap((collision) => {
      const targetId = resolveTrackCollision({
        candidateId: String(collision.id),
        items: tracks,
        disabledTracks,
        trackToSort,
        moveSubTrackEnabled: subTrackReorganizationFlag,
      });

      if (!targetId || resolvedTargets.has(targetId)) return [];

      resolvedTargets.add(targetId);
      return [targetId === collision.id ? collision : { id: targetId }];
    });
  };

  const dropHighlight = (track: TrackPreview) =>
    resolveDropHighlight({
      track,
      items: tracks,
      trackToSort,
      overGroupId,
      moveSubTrackEnabled: subTrackReorganizationFlag,
    });

  // Index/isLast below are derived from the `tracks` prop, not the shared
  // track-rows (rowIndexByTrackId/lastTrackIndex), on purpose: using the
  // shared map here previously caused rows to desync during the
  // reorder-to-refetch window, since it still reflected the pre-drag order.
  // See 47fa721ca.
  const collapsedTrackIds = new Set(collapsedTracks);
  const nonCollapsedTracks = tracks.filter(
    (t) => !isHiddenSubTrack(t, collapsedTrackIds),
  );
  const nonCollapsedTrackIndexById = new Map(
    nonCollapsedTracks.map((t, i) => [t.id, i]),
  );

  return (
    <div
      className="flex-grow overflow-y-auto"
      style={{
        paddingBottom: BOTTOM_PADDING,
      }}
      ref={scrollRef}
      onScroll={onScroll}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={customCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragCancel={() => {
          setTrackToSort(null);
          setDisabledTracks(null);
          setOverGroupId(null);
        }}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        {/* useSortable only drives a row's own `transform` while the pointer
            stays inside its origin SortableContext (see TrackSidePanelItem).
            A sub-track dragged toward another parent's group leaves that
            context, so this overlay is what keeps a preview visible under
            the pointer for the rest of the drag. */}
        <DragOverlay>
          {trackToSort ? (
            <div
              className="flex flex-row items-center pl-6 pr-3 bg-white border shadow-sm"
              style={{ height: TRACK_HEIGHT }}
            >
              <MdDragIndicator
                aria-hidden="true"
                size={18}
                className="mr-2 text-gray-500"
              />
              <p className="text-xs font-semibold truncate">
                {trackToSort.name}
              </p>
            </div>
          ) : null}
        </DragOverlay>
        {/* Group parent tracks */}
        <SortableContext
          items={nonCollapsedTracks.filter((t) => !t.parentTrackId)}
          strategy={verticalListSortingStrategy}
        >
          {nonCollapsedTracks.map((track, index) => {
            if (track.parentTrackId) return null;
            const subTracksToShow = tracks.filter(
              (t) =>
                t.parentTrackId === track.id &&
                !isHiddenSubTrack(t, collapsedTrackIds),
            );

            return (
              <React.Fragment key={track.id}>
                {/* Parent track */}
                <TrackSidePanelItem
                  key={track.id}
                  track={track}
                  allTracks={tracks}
                  setTrackForEdit={setTrackForEdit}
                  index={index + 1}
                  isLast={index === lastTrackIndex}
                  parentTrack={tracks.find((t) => t.id === track.parentTrackId)}
                  openAddTrackDrawer={openAddTrackDrawer}
                  active={trackToSort?.id === track.id}
                  hasSubtracks={
                    tracks.filter((t) => t.parentTrackId === track.id).length >
                    0
                  }
                  disabled={!isDropTarget(track.id)}
                  dropHighlight={dropHighlight(track)}
                  toggleCollapse={toggleTrackCollapse}
                  isCollapsed={collapsedTracks.includes(track.id)}
                  refetchTracks={refetchTracks}
                  openMoveSubTrack={openMoveSubTrack}
                />
                {/* Group subtracks under their parent */}
                <SortableContext
                  items={subTracksToShow}
                  strategy={verticalListSortingStrategy}
                >
                  {subTracksToShow.map((childTrack) => {
                    const globalIndex =
                      nonCollapsedTrackIndexById.get(childTrack.id) ?? -1;

                    return (
                      <TrackSidePanelItem
                        key={childTrack.id}
                        track={childTrack}
                        allTracks={tracks}
                        setTrackForEdit={setTrackForEdit}
                        index={globalIndex + 1}
                        isLast={globalIndex === lastTrackIndex}
                        parentTrack={track}
                        openAddTrackDrawer={openAddTrackDrawer}
                        active={trackToSort?.id === childTrack.id}
                        disabled={!isDropTarget(childTrack.id)}
                        dropHighlight={dropHighlight(childTrack)}
                        isCollapsed={false}
                        toggleCollapse={toggleTrackCollapse}
                        refetchTracks={refetchTracks}
                        openMoveSubTrack={openMoveSubTrack}
                      />
                    );
                  })}
                </SortableContext>
              </React.Fragment>
            );
          })}
        </SortableContext>
      </DndContext>
      {children}
    </div>
  );
}
